<script setup lang="ts">
import { BaseHeader, type BaseHeaderImage } from '@components/base';
import { computed } from 'vue';

type ListItem = { text?: string } | string;

const props = withDefaults(
  defineProps<{
    headingHtml?: string;
    headingObjects?: BaseHeaderImage[] | string;
    listItems?: ListItem[] | string;
    tip?: string;
    ctaLabelHtml?: string;
    ctaFallbackText?: string;
    ctaHref?: string;
    ctaTarget?: string;
    ctaBg?: string | null;
    imageUrl?: string;
    imageAlt?: string;
    imageBg?: string | null;
  }>(),
  {
    headingHtml: '',
    headingObjects: () => [],
    listItems: () => [],
    tip: '',
    ctaLabelHtml: '',
    ctaFallbackText: '',
    ctaHref: '',
    ctaTarget: '_self',
    ctaBg: '',
    imageUrl: '',
    imageAlt: '',
    imageBg: '',
  },
);

function parseHeadingObjects(raw: BaseHeaderImage[] | string | undefined): BaseHeaderImage[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    let s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
    } catch {
      if (s.includes('&quot;')) {
        s = s.replace(/&quot;/g, '"');
        try {
          const parsed = JSON.parse(s) as unknown;
          return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
        } catch {
          return [];
        }
      }
      return [];
    }
  }
  return [];
}

function parseListItems(raw: ListItem[] | string | undefined): string[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        return (item?.text ?? '').trim();
      })
      .filter(Boolean);
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      if (!Array.isArray(parsed)) return [];
      return (parsed as ListItem[])
        .map((item) => {
          if (typeof item === 'string') return item.trim();
          return (item?.text ?? '').trim();
        })
        .filter(Boolean);
    } catch {
      return [];
    }
  }
  return [];
}

const headingObjectsList = computed(() => parseHeadingObjects(props.headingObjects));
const list = computed(() => parseListItems(props.listItems));

const headingContentClass = 'font-inter-tight text-30 font-semibold leading-[1.35] tracking-[-0.01em] max-w-[760px] lg:text-34 lg:font-semibold lg:leading-[1.15] [&_p]:m-0 [&_p]:leading-[inherit]';

const ctaLabelUsesHtml = computed(() => {
  const raw = props.ctaLabelHtml ?? '';
  return raw.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
});

const ctaStyle = computed(() => {
  const bg = props.ctaBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const imagePanelStyle = computed(() => {
  const bg = props.imageBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const show = computed(() => {
  const headingText = (props.headingHtml ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  const hasHeading = headingText.length > 0;
  const hasList = list.value.length > 0;
  const hasCta = (props.ctaHref ?? '').trim().length > 0;
  const hasImage = (props.imageUrl ?? '').trim().length > 0;
  const tipPlain = (props.tip ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  const hasTip = tipPlain.length > 0;
  // Match About-style sections: show if any primary content exists (avoid hiding when Twig passed image/tip only).
  return hasHeading || hasList || hasCta || hasImage || hasTip;
});
</script>

<template>
  <section
    v-if="show"
    class="pricing-cta1 mb-32 mt-64 font-inter md:pt-20 lg:mb-32 lg:mt-80"
  >
    <div class="container">
      <div class="mx-0 grid gap-20 md:gap-24 lg:mx-32 lg:grid-cols-2 lg:items-center">
        <div class="mt-0 px-8 text-off-black-500 md:px-0 lg:mt-48 lg:px-20">
          <div class="flex flex-col">
            <BaseHeader
              v-if="headingHtml"
              as="h2"
              :unwrap-outer-paragraph="true"
              :content="headingHtml"
              :objects="headingObjectsList"
              :content-class="headingContentClass"
            />

            <div
              v-if="imageUrl"
              class="relative my-32 w-full lg:mt-16 lg:hidden"
            >
              <div
                class="relative w-full overflow-hidden rounded-[20px] pt-[100%] sm:pt-[80%]"
                :style="imagePanelStyle"
              >
                <img
                  :src="imageUrl"
                  :alt="imageAlt || ''"
                  class="absolute inset-0 size-full rounded-[20px] object-cover"
                  loading="lazy"
                  decoding="async"
                >
              </div>
            </div>

            <div class="max-w-[720px] lg:my-48">
              <ul
                v-if="list.length"
                class="space-y-20 font-inter text-14 font-normal leading-[1.3] lg:space-y-24 lg:text-16"
              >
                <li
                  v-for="(item, index) in list"
                  :key="`${index}-${item}`"
                  class="flex items-start gap-8"
                >
                  <span class="-mt-1 shrink-0 text-copper-500">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="size-24"
                    >
                      <path
                        d="M15.5 10L10.8333 15L8.5 12.9339M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                        stroke="#AC6D3F"
                        stroke-width="1.75"
                        stroke-linecap="round"
                      />
                    </svg>
                  </span>
                  <span>{{ item }}</span>
                </li>
              </ul>

              <div
                v-if="ctaHref"
                :class="{ 'my-28 lg:my-48': list.length > 0 }"
              >
                <a
                  :href="ctaHref"
                  :target="ctaTarget || '_self'"
                  class="mx-auto flex w-full max-w-full items-center justify-center rounded-full px-28 py-14 font-inter-tight text-14 font-semibold tracking-wide text-white transition-opacity hover:opacity-90 lg:mx-0 lg:px-32 lg:py-16 lg:text-16 xl:w-fit"
                  :class="{ 'bg-off-black-500': !ctaBg || !ctaBg.trim() }"
                  :style="ctaStyle"
                >
                  <span
                    v-if="ctaLabelUsesHtml"
                    class="[&_p]:m-0"
                    v-html="ctaLabelHtml"
                  />
                  <template v-else>{{ ctaFallbackText }}</template>
                </a>
              </div>

              <p
                v-if="tip"
                class="text-off-black-50 mt-28 font-inter text-10 font-normal leading-[1.3]"
                v-html="tip"
              />
            </div>
          </div>
        </div>

        <div
          v-if="imageUrl"
          class="relative mx-auto hidden w-full max-w-[530px] md:px-0 lg:mx-0 lg:block lg:justify-self-end"
        >
          <div
            class="relative h-[600px] w-full overflow-hidden rounded-[28px]"
            :style="imagePanelStyle"
          >
            <img
              :src="imageUrl"
              :alt="imageAlt || ''"
              class="absolute inset-0 size-full rounded-[28px] object-cover"
              loading="lazy"
              decoding="async"
            >
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
